import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import { determineIsDesktop, filterNullUndefined } from '@domgen/dgx-fe-common';
import { ModalService } from '@domgen/dgx-fe-components/modal';
import { GAService } from '@domgen/dgx-fe-ga';
import { ComponentStore } from '@ngrx/component-store';
import {
  getActiveFeatureFlagVariant,
  RegistrationFacade,
  setLocalStorageConfirmationData,
  setLocalStorageRegistriaData,
  USCheckoutService,
} from '@us-registration/data-access';
import { AchBannerTextVariant } from '@us-registration/util-models';

import {
  CmsProtectionDetailsPage,
  CmsProtectionDetailsUpsellModal,
  PrepaySubscriptionVariant,
  ProtectionDetailsUpsellModalData,
  ProtectionDetailsUpsellModalResult,
  RegisteredItem,
  RegisteredItemProductDetails,
  RegistrationApplianceDetails,
  RegistrationRoutes,
  RegistrationSystemCode,
  RegistriaUsersResponse,
  SerializationCodeResponse,
  USPostHogTrackingEvents,
  WasherEnhancementVariant,
} from '@us-registration/util-models';
import posthog from 'posthog-js';
import {
  combineLatest,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { washerApplianceContent } from '../../components/constants/appliance-content.constant';
import { ProtectionDetailsUpsellModalComponent } from '../../components/protection-details-upsell-modal/protection-details-upsell-modal.component';
import { ApplianceContent } from '../../models/appliance-content.interface';

export interface ViewModel {
  cmsData: CmsProtectionDetailsPage;
  ctaUpsellLoading: boolean;
  displayPromotionalBannerTextVariant?: boolean;
  hasRegisteredItemOutOfGuarantee: boolean;
  isDesktop: boolean;
  prepaySubscriptionVariant: PrepaySubscriptionVariant | null;
  promotionalPriceEnabled: boolean;
  protectionDetailsUpsellModalData?: ProtectionDetailsUpsellModalData;
  registeredItem?: RegisteredItem;
  registeredItemHasAnnualPayment: boolean;
  registeredItemProductDetails?: RegisteredItemProductDetails;
  showWasherEnhancement: boolean;
  washerApplianceContent: ApplianceContent;
  registeredItemAnnualProductDetails?: RegisteredItemProductDetails;
}

export interface ComponentState {
  ctaUpsellLoading: boolean;
  promotionalPriceEnabled: boolean;
  registeredItem?: RegisteredItem;
  systemCode: RegistrationSystemCode;
  isRegisteredItemWasher: boolean;
  showWasherEnhancement: boolean;
}

@Injectable()
export class ProtectionDetailsComponentService extends ComponentStore<ComponentState> {
  private readonly ctaUpsellLoading$: Observable<boolean> = this.select(
    (state) => state.ctaUpsellLoading
  );

  private readonly promotionalPriceEnabled$: Observable<boolean> = this.select(
    (state) => state.promotionalPriceEnabled
  );
  private readonly isRegisteredItemWasher$: Observable<boolean> = this.select(
    (state) => state.isRegisteredItemWasher
  );
  readonly showWasherEnhancement$: Observable<boolean> = this.select(
    (state) => state.showWasherEnhancement
  );

  private readonly cmsProtectionDetailsUpsellModal$: Observable<CmsProtectionDetailsUpsellModal> =
    combineLatest([this.cmsContainer.content$]).pipe(
      map(([cmsData]) => ({
        protectionDetailsModalTitle: cmsData.protectionDetailsModalTitle,
        protectionDetailsModalSubtitle: cmsData.protectionDetailsModalSubtitle,
        protectionDetailsModalBenefitsListTitle:
          cmsData.protectionDetailsModalBenefitsListTitle,
        protectionDetailsModalBenefitsListItems:
          cmsData.protectionDetailsModalBenefitsListItems,
      }))
    );

  protectionDetailsUpsellModalData$: Observable<ProtectionDetailsUpsellModalData> =
    combineLatest({
      cmsProtectionDetailsUpsellModal: this.cmsProtectionDetailsUpsellModal$,
      merchandiseBrand: this.registrationFacade.registeredItemMerchandiseBrand$,
      prepaySubscriptionVariant: of(this.prepaySubscriptionVariant),
      promotionalPriceEnabled: this.promotionalPriceEnabled$,
      registeredItemHasAnnualPayment:
        this.registrationFacade.registeredItemHasAnnualPayment$,
      registeredItemProductDetails:
        this.registrationFacade.registeredItemProductDetails$,
      registeredItemAnnualProductDetails:
        this.registrationFacade.registeredItemAnnualProductDetails$,
      registeredItemProducts: this.registrationFacade.registeredItemProducts$,
      systemCode: this.registrationFacade.registrationSystemCode$,
    });

  readonly vm$: Observable<ViewModel> = combineLatest({
    cmsData: this.cmsContainer.content$,
    ctaUpsellLoading: this.ctaUpsellLoading$,
    displayPromotionalBannerTextVariant: of(this.getPromotionalBannerText),
    isDesktop: determineIsDesktop(),
    hasRegisteredItemOutOfGuarantee:
      this.registrationFacade.hasRegisteredItemOutOfGuarantee$,
    prepaySubscriptionVariant: of(this.prepaySubscriptionVariant),
    promotionalPriceEnabled: this.promotionalPriceEnabled$,
    registeredItem: this.registrationFacade.registeredItem$,
    registeredItemProductDetails:
      this.registrationFacade.registeredItemProductDetails$,
    registeredItemAnnualProductDetails:
      this.registrationFacade.registeredItemAnnualProductDetails$,
    registeredItemHasAnnualPayment:
      this.registrationFacade.registeredItemHasAnnualPayment$,
    showWasherEnhancement: this.showWasherEnhancement$,
    washerApplianceContent: of(washerApplianceContent),
  });

  constructor(
    private readonly cmsContainer: CmsContainer<CmsProtectionDetailsPage>,
    private readonly destroyRef: DestroyRef,
    private readonly modalService: ModalService,
    private readonly registrationFacade: RegistrationFacade,
    private readonly router: Router,
    private readonly checkoutService: USCheckoutService,
    private readonly gaService: GAService
  ) {
    super({
      ctaUpsellLoading: false,
      promotionalPriceEnabled: false,
      systemCode: RegistrationSystemCode.InternetProductRegistration,
      showWasherEnhancement: false,
      isRegisteredItemWasher: false,
    });
  }

  private readonly updateRegisteredItem = this.updater(
    (state: ComponentState, registeredItem: RegisteredItem | undefined) => ({
      ...state,
      registeredItem,
    })
  )(this.registrationFacade.registeredItem$);

  private readonly updateSystemCode = this.updater(
    (state: ComponentState, systemCode: RegistrationSystemCode) => ({
      ...state,
      systemCode,
    })
  )(this.registrationFacade.registrationSystemCode$);

  private readonly updatePromotionalPriceEnabled = this.updater(
    (state: ComponentState, promotionalPriceEnabled: boolean) => ({
      ...state,
      promotionalPriceEnabled,
    })
  )(
    this.cmsContainer.content$.pipe(map((cmsData) => cmsData.promotionalPrice))
  );

  triggerRegistrationUpsellModal() {
    posthog.capture(
      USPostHogTrackingEvents.ProtectionDetailsPageRegisterOnlyCtaClicked
    );

    this.protectionDetailsUpsellModalData$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((protectionDetailsUpsellModalData) =>
          this.openProtectionDetailsUpsellModal(
            protectionDetailsUpsellModalData
          )
        ),
        filterNullUndefined(),
        tap((result: ProtectionDetailsUpsellModalResult) => {
          if (result === ProtectionDetailsUpsellModalResult.RegistrationOnly) {
            this.continueToRegistrationOnly();
          }

          if (result === ProtectionDetailsUpsellModalResult.ExtraProtection) {
            this.continueToOrderConfirmation();
          }
        })
      )
      .subscribe();
  }

  handlePaymentOptionAction(clickedPrepayCta: boolean): void {
    this.continueToOrderConfirmation(clickedPrepayCta);
  }

  continueToOrderConfirmation(clickedPrepayCta = false): void {
    posthog.capture('protection-details-upsell-cta-clicked');

    posthog.capture(
      clickedPrepayCta
        ? USPostHogTrackingEvents.ProtectionDetailsPageAnnualUpsellCtaClicked
        : USPostHogTrackingEvents.ProtectionDetailsPageMonthlyUpsellCtaClicked
    );

    this.checkoutService.redirectToCheckout(
      this.promotionalPriceEnabled,
      this.systemCode,
      clickedPrepayCta
    );

    this.updateCtaUpsellLoading(true);

    if (this.registeredItemAppliance) {
      this.gaService.tag({
        pagename: 'protection-details',
        event: 'add_to_cart',
        ecommerce: {
          items: [
            {
              item_brand: this.registeredItemAppliance.brand,
              item_category: this.registeredItemAppliance.type,
              appliance_type: this.registeredItemAppliance.type,
              appliance_purchase_cost:
                this.registeredItemAppliance.purchasePrice,
              appliance_purchase_date:
                this.registeredItemAppliance.purchaseDate,
              appliance_warranty: true,
              item_id: '',
              item_name: `${this.registeredItemAppliance.brand} ${this.registeredItemAppliance.type}`,
              price: this.registeredItemAppliance.purchasePrice,
              quantity: 1,
            },
          ],
        },
      });
    }
  }

  private readonly setLocalStorageConfirmationData: Subscription = this.effect(
    (
      trigger$: Observable<
        [RegisteredItem | undefined, SerializationCodeResponse | undefined]
      >
    ) =>
      trigger$.pipe(
        filterNullUndefined(),
        tap(([registeredItem, serializationCodeResponse]) => {
          setLocalStorageConfirmationData(
            registeredItem as RegisteredItem,
            serializationCodeResponse as SerializationCodeResponse
          );
        })
      )
  )(
    combineLatest([
      this.registrationFacade.registeredItem$,
      this.registrationFacade.serializationCodeResponse$,
    ])
  );

  private readonly setLocalStorageRegistrationData: Subscription = this.effect(
    (trigger$: Observable<RegistriaUsersResponse | undefined>) =>
      trigger$.pipe(
        filterNullUndefined(),
        tap((registriaUserResponse: RegistriaUsersResponse) =>
          setLocalStorageRegistriaData(registriaUserResponse)
        )
      )
  )(this.registrationFacade.registriaUserResponse$);

  private readonly updateCtaUpsellLoading = this.updater(
    (state: ComponentState, ctaUpsellLoading: boolean) => ({
      ...state,
      ctaUpsellLoading,
    })
  );

  private readonly updateIsRegisteredItemWasher = this.updater(
    (state: ComponentState, isRegisteredItemWasher: boolean) => ({
      ...state,
      isRegisteredItemWasher,
    })
  )(
    this.registrationFacade.registeredItem$.pipe(
      filterNullUndefined(),
      map((registeredItem) => registeredItem.merchandiseType === 'Washer')
    )
  );

  readonly updateShowWasherEnhancement = this.updater(
    (state: ComponentState, showWasherEnhancement: boolean) => ({
      ...state,
      showWasherEnhancement,
    })
  )(
    this.isRegisteredItemWasher$.pipe(
      map((isWasher: boolean) => {
        const variant = getActiveFeatureFlagVariant(
          'washer-enhancement-flag',
          WasherEnhancementVariant
        );

        return variant === WasherEnhancementVariant.Enhanced && isWasher;
      })
    )
  );

  continueToRegistrationOnly(): void {
    posthog.capture('protection-details-upsell-cta-registration-only');
    this.router.navigateByUrl(RegistrationRoutes.RegistrationConfirmation);
  }

  openProtectionDetailsUpsellModal(
    protectionDetailsUpsellModalData: ProtectionDetailsUpsellModalData
  ): Observable<ProtectionDetailsUpsellModalResult | undefined> {
    return this.modalService.open<
      ProtectionDetailsUpsellModalComponent,
      ProtectionDetailsUpsellModalData,
      ProtectionDetailsUpsellModalResult
    >(ProtectionDetailsUpsellModalComponent, {
      data: {
        cmsProtectionDetailsUpsellModal:
          protectionDetailsUpsellModalData.cmsProtectionDetailsUpsellModal,
        merchandiseBrand: protectionDetailsUpsellModalData.merchandiseBrand,
        prepaySubscriptionVariant:
          protectionDetailsUpsellModalData.prepaySubscriptionVariant,
        promotionalPriceEnabled:
          protectionDetailsUpsellModalData.promotionalPriceEnabled,
        registeredItemHasAnnualPayment:
          protectionDetailsUpsellModalData.registeredItemHasAnnualPayment,
        registeredItemProductDetails:
          protectionDetailsUpsellModalData.registeredItemProductDetails,
        registeredItemAnnualProductDetails:
          protectionDetailsUpsellModalData.registeredItemAnnualProductDetails,
        registeredItemProducts:
          protectionDetailsUpsellModalData.registeredItemProducts,
        systemCode: protectionDetailsUpsellModalData.systemCode,
      },
      maxWidth: 'max-content',
      maxHeight: 'max-content',
    }).afterClosed$;
  }

  get registeredItemAppliance(): RegistrationApplianceDetails | undefined {
    return this.get((state) => state.registeredItem)?.appliances[0];
  }

  get promotionalPriceEnabled(): boolean {
    return this.get((state) => state.promotionalPriceEnabled);
  }

  get systemCode(): RegistrationSystemCode {
    return this.get((state) => state.systemCode);
  }

  get prepaySubscriptionVariant(): PrepaySubscriptionVariant | null {
    return getActiveFeatureFlagVariant(
      'prepay-subscription',
      PrepaySubscriptionVariant
    );
  }

  get getPromotionalBannerText() {
    return (
      getActiveFeatureFlagVariant(
        'ach-promotional-banner-text',
        AchBannerTextVariant
      ) === AchBannerTextVariant.Variant
    );
  }
}
